import type { CloudPlusCountChangeErrorPayload, RpErrorTypes } from "@/generated/models";
import { createSharedComposable, useTimeoutFn } from "@vueuse/core";
import * as v from "valibot";
import { computed, readonly, ref, watch } from "vue";
import { asError } from "@/common/lib";

interface ErrorsPayloadMap {
  [RpErrorTypes.LicenseAuditFailed]: undefined;
  [RpErrorTypes.SubInstanceLicenseAuditFailed]: undefined;
  [RpErrorTypes.ClickthroughPublishRequiresLicense]: undefined;
  [RpErrorTypes.NoPermissionToAccess]: undefined;
  [RpErrorTypes.ViewOnlyFilesystem]: { FilesystemName?: string };

  [RpErrorTypes.CloudAuthorSeatRequired]: CloudPlusCountChangeErrorPayload;
  [RpErrorTypes.CloudAuthorRoleRequired]: CloudPlusCountChangeErrorPayload;
  [RpErrorTypes.CloudPlusRequired]: CloudPlusCountChangeErrorPayload;

  [key: number]: unknown;
}

export type AccessControlError = {
  [K in keyof ErrorsPayloadMap]: {
    type: K;
    payload: ErrorsPayloadMap[K];
  }
}[keyof ErrorsPayloadMap];

const RpErrorCauseSchema = v.object({
  ErrorType: v.number(),
  Payload: v.optional(v.any()),
});

function useAccessControlFn() {
  const accessControlError = ref<AccessControlError>();

  async function executeCheckedEndpoint<T>(endpoint: () => Promise<T>): Promise<T> {
    try {
      return await endpoint();
    } catch (e) {
      const err = asError(e);

      if (v.is(RpErrorCauseSchema, err.cause)) {
        accessControlError.value = {
          type: err.cause.ErrorType as RpErrorTypes,
          payload: err.cause.Payload,
        };
        err.message = "";
        throw err;
      }

      throw e;
    }
  }

  function clear() {
    accessControlError.value = undefined;
  }

  return {
    executeCheckedEndpoint,
    accessControlError: readonly(accessControlError),
    clear,
  };
}

export const useAccessControl = createSharedComposable(useAccessControlFn);

export function useAccessControlErrorsFilter<T extends RpErrorTypes>(errorTypes: T | T[]) {
  const { accessControlError } = useAccessControl();

  const types = Array.isArray(errorTypes) ? errorTypes : [errorTypes];

  const error = computed(() => {
    const error = accessControlError.value;
    if (!error) return undefined;

    if (!types.includes(error.type as T)) {
      return undefined;
    }

    return {
      payload: error.payload,
      type: error.type,
    } as { type: T; payload: ErrorsPayloadMap[T] };
  });

  const payload = computed(() => error.value?.payload);

  const isOpen = ref(false);

  // when error captured, open dialog
  watch(error, newValue => {
    isOpen.value = !!newValue;
  }, { immediate: true });

  // track if dialog is in error state
  const isError = ref(!!error.value);

  // if dialog has been closed for 1 second, clear error state
  // this is necessary to apply `v-if` on the dialog component itself so that dialog stacks correctly on top of other dialogs,
  // but still use v-model:open="isOpen" to play exit animations
  const {
    start: scheduleClearError,
    stop,
  } = useTimeoutFn(() => {
    isError.value = false;
  }, 1_000, { immediate: false });

  watch(isOpen, (newVal, oldVal) => {
    if (!newVal && oldVal) {
      // dialog closed
      scheduleClearError();
    } else if (newVal && !oldVal) {
      // dialog re-opened
      isError.value = true;
      stop();
    }
  });

  return {
    error,
    payload,
    isOpen,
    isError,
  };
}
